import _ from 'lodash';
import { funcIndexConfig, getValveIrrigationRouteCount, MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE } from '../Constants/constants';
import { buildIoOverwrites } from './io_overwrite_settings';
import { getControllerApiVersion } from './utils';
import { getCirculationCount } from './ventilation_utils';

export function getMultiIoIndexConfig(controller) {
  // Native Industrial Pro has its own API history and per-zone capacities.
  if (controller?.type === 6000) return funcIndexConfig;
  if (controller?.type !== 5155) return {};
  const version = getControllerApiVersion(controller);
  return Object.fromEntries(Object.entries(funcIndexConfig).filter(
    ([, config]) => version >= (config.minVersion ?? 'ADF')
  ));
}

export function getMultiIoIndexCount(controller, parent, func) {
  const config = getMultiIoIndexConfig(controller)[func];
  if (!config) return 0;
  if (Number(func) === MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE)
    return getValveIrrigationRouteCount(controller, parent);
  if (Number(func) === 43) return getCirculationCount(controller, parent);
  // These indices come from their existing mappings rather than a legacy counter.
  if ([4, 120].includes(Number(func))) return -1;
  const counters = (controller?.type === 6000 ? parent : controller)?.multi_io_counters;
  return counters?.[config.countKey] ?? (controller?.type === 6000 ? -1 : 0);
}

export function supportsMultiIoOverrides(controller) {
  return controller?.type === 6000 ||
    (controller?.type === 5155 && getControllerApiVersion(controller) >= 'AE');
}

export function supportsMultiIoFunctionSets(controller) {
  return controller?.type === 6000 ||
    (controller?.type === 5155 && getControllerApiVersion(controller) >= 'AEC');
}

// Reconnect keeps controller/zone proxies but unmounts the editor. Keep only
// submission bookkeeping in this session; never publish it as controller state.
const editorSessions = new WeakMap();
export function getMultiIoEditorSession(controller, parent) {
  if (!editorSessions.has(controller)) editorSessions.set(controller, new WeakMap());
  const zones = editorSessions.get(controller);
  if (!zones.has(parent)) zones.set(parent, {
    previousSaved: { current: null },
    submitted: { current: {} }
  });
  return zones.get(parent);
}

const editableFields = ['func', 'func_index', 'memo'];

const fieldKey = (slave, port, field) => `multi_out_${slave}_${port}_${field}`;
const sameValue = (field, left, right) =>
  field === 'memo' ? _.isEqual(left, right) : String(left) === String(right);

// Keep editable assignment values when another client changes the same physical
// port. Runtime override values/countdowns remain owned by their reconciliation.
export function retainMultiIoReadback(current, saved, previous, drafts = {}, pending = {}) {
  const next = _.cloneDeep(drafts);
  for (const [slave, ports] of Object.entries(saved)) {
    for (const [port, row] of Object.entries(ports)) {
      const local = current?.[slave]?.[port];
      const old = previous?.[slave]?.[port];
      if (!local || !old || ['_index', 'type_id', 'zone'].some((key) => old[key] !== row[key])) continue;
      for (const field of editableFields) {
        const key = fieldKey(slave, port, field);
        if (pending[key] || sameValue(field, row[field], old[field])) continue;
        if (sameValue(field, local[field], row[field])) delete next[key];
        else next[key] = _.cloneDeep(local[field]);
      }
    }
  }
  return next;
}

const changedFields = (current, saved, fields) =>
  !saved || fields.some((field) => !sameValue(field, current[field], saved[field]));
const isRouteValve = (func) => Number(func) === MULTI_IO_FUNCTION_VALVE_IRRIGATION_ROUTE;
const supportedRouteIndex = (index, count) => {
  // Select controls and restored drafts can contain numeric strings. Do not
  // interpret an empty/missing value or Boolean as index zero.
  if (typeof index !== 'number' && !(typeof index === 'string' && /^\d+$/.test(index)))
    return false;
  const value = Number(index);
  return Number.isInteger(value) && value >= 0 && value < count;
};

// Validate edits at submission time: the advertised capacity may have changed
// since the choices were shown. Unchanged unfamiliar readback remains usable.
export function areValveRouteAssignmentsSupported(controller, parent, values, saved) {
  const count = getValveIrrigationRouteCount(controller, parent);
  return Object.entries(values ?? {}).every(([slave, ports]) =>
    Object.entries(ports).every(([port, settings]) =>
      !isRouteValve(settings.func) ||
      !changedFields(settings, saved?.[slave]?.[port], ['func', 'func_index']) ||
      supportedRouteIndex(settings.func_index, count)
    )
  );
}

export function areValveRouteOverridesSupported(
  controller, parent, functions, savedFunctions, ports, savedPorts
) {
  const count = getValveIrrigationRouteCount(controller, parent);
  const functionsSupported = (functions?.oios ?? []).every((entry) => {
    if (!entry || entry.__deleted__ || Number(entry.select) === 0 || !isRouteValve(entry.function))
      return true;
    const saved = (savedFunctions?.oios ?? []).find((candidate) => candidate &&
      ['zone', 'function', 'func_index'].every((field) => sameValue(field, entry[field], candidate[field])));
    return !changedFields(entry, saved, ['select', 'val_ana', 'add_sett', 'timeout']) ||
      supportedRouteIndex(entry.func_index, count);
  });
  return functionsSupported && Object.entries(ports ?? {}).every(([slave, entries]) =>
    Object.entries(entries).every(([port, entry]) =>
      Number(entry.overwr_select) === 0 || !isRouteValve(entry.func) ||
      !changedFields(entry, savedPorts?.[slave]?.[port],
        ['func', 'func_index', 'overwr_select', 'overwr_val_ana', 'overwr_timeout']) ||
      supportedRouteIndex(entry.func_index, count)
    )
  );
}

export function areValveRouteFunctionSetsSupported(controller, parent, values, saved) {
  const count = getValveIrrigationRouteCount(controller, parent);
  return Object.entries(values ?? {}).every(([id, settings]) => {
    if (!settings.enabled) return true;
    const previous = saved?.[id];
    // Match references by content, not member position: removing another member
    // must not make an unchanged unfamiliar route look newly configured.
    const unchanged = previous?.enabled &&
      !changedFields(settings, previous, ['logic_operation', 'target_zone'])
      ? (previous.members ?? []).filter((member) => member.enabled)
      : [];
    return (settings.members ?? []).every((member) => {
      if (!member.enabled || !isRouteValve(member.function)) return true;
      const index = unchanged.findIndex((candidate) =>
        !changedFields(member, candidate, ['function', 'func_index', 'source_zone', 'invert_logic']));
      if (index !== -1) {
        unchanged.splice(index, 1);
        return true;
      }
      return supportedRouteIndex(member.func_index, count);
    });
  });
}

// A full-category save must start with live settings, especially live overwrite
// countdowns. Apply only drafts and still-unconfirmed assignment/memo edits.
export function buildMultiIoSettings(
  live, edited, drafts = {}, pending = {}, permissions = {}, indexConfig = null
) {
  const result = permissions.overwrites
    ? buildIoOverwrites(live, edited, drafts)
    : _.cloneDeep(live);
  if (!permissions.settings) return result;
  for (const [slave, ports] of Object.entries(result)) {
    for (const [port, row] of Object.entries(ports)) {
      if (!edited?.[slave]?.[port] || edited[slave][port]._index !== row._index) continue;
      for (const field of editableFields) {
        // Hidden instance selectors must not apply a restored or pending edit.
        if (field === 'func_index' && indexConfig && !indexConfig[edited[slave][port].func]) continue;
        const key = fieldKey(slave, port, field);
        if (Object.prototype.hasOwnProperty.call(drafts, key)) {
          row[field] = field === 'memo' && drafts[key] === -1 ? '' : drafts[key];
        } else if (pending[key] && pending[key].index === row._index)
          row[field] = pending[key].value;
      }
    }
  }
  return result;
}

// Keep only the latest submitted value per field, including a save that reverts
// an earlier pending edit to the currently cached value.
export function recordMultiIoSubmission(values, saved, pending = {}) {
  const next = {};
  for (const [slave, ports] of Object.entries(values)) {
    for (const [port, settings] of Object.entries(ports)) {
      for (const field of editableFields) {
        const key = fieldKey(slave, port, field);
        if (pending[key] || !sameValue(field, settings[field], saved?.[slave]?.[port]?.[field])) {
          next[key] = { slave, port, field, index: settings._index, value: settings[field] };
        }
      }
    }
  }
  return next;
}

export function reconcileMultiIoSubmissions(pending, saved, previousSaved) {
  return Object.fromEntries(
    Object.entries(pending).filter(([, submission]) => {
      const { slave, port, field, index, value } = submission;
      const settings = saved?.[slave]?.[port];
      if (!settings || settings._index !== index) return false;
      // An unchanged cached value is not confirmation of a new save. In
      // particular, telemetry must not acknowledge a pending revert prematurely.
      return (
        !sameValue(field, settings[field], value) ||
        sameValue(field, settings[field], previousSaved?.[slave]?.[port]?.[field])
      );
    })
  );
}

// Refresh each field only when its saved value changes. Unchanged readbacks may
// still predate a save; they must not undo the submitted value in the editor.
export function syncMultiIoSettings(current, saved, previousSaved, drafts = {}, pending = {}) {
  const next = _.cloneDeep(saved);
  for (const [slave, ports] of Object.entries(next)) {
    for (const [port, settings] of Object.entries(ports)) {
      const local = current?.[slave]?.[port];
      const previous = previousSaved?.[slave]?.[port];
      if (!local || !previous || previous._index !== settings._index) continue;
      for (const field of editableFields) {
        const key = fieldKey(slave, port, field);
        const dirty = Object.prototype.hasOwnProperty.call(drafts, key);
        if (dirty || pending[key] || sameValue(field, settings[field], previous[field])) {
          settings[field] = local[field];
        }
      }
    }
  }
  return _.isEqual(current, next) ? current : next;
}
